import { useMutation } from "@apollo/client";
import { Dialog, Classes, Button, FormGroup, MenuItem, InputGroup, Pre, H4, Card, H5, H6 } from "@blueprintjs/core";
import { Select, ItemRenderer } from "@blueprintjs/select";
import React, { useCallback, useContext, useRef, useState } from "react";
import { useEffect } from "react";
import { UPSERT_POLICY } from "graphql/mutations";
import { GET_ALARM_POLICIES } from "graphql/queries";
import { usePolicyActionsByType } from "hooks/usePolicy";

import { SelectedCompanyContext } from "utils/context";
import CustomLoader from "components/CustomLoader";
import "./PolicyDialog.scss";
import { AlarmPolicy, PolicyType, PolicyAction, AlarmPolicyInput } from "types";
import _ from "lodash";
import { PolicyToaster } from "utils/toasters";

type Props = { toggle: () => void; isOpen: boolean; isEdit: boolean; policy?: AlarmPolicy };

type PolicyOption = {
    text: string;
    value: PolicyType;
};

const TypeSelect = Select.ofType<PolicyOption>();

const renderType: ItemRenderer<PolicyOption> = (t, { handleClick, modifiers }) => {
    if (!modifiers.matchesPredicate) {
        return null;
    }

    return <MenuItem active={modifiers.active} key={t.value} onClick={handleClick} text={t.text} />;
};

export default function PolicyDialog({ toggle, isOpen, isEdit, policy }: Props) {
    const [selectedCompany] = useContext(SelectedCompanyContext);
    const [addLoading, setAddLoading] = useState(false);
    //default to an alarm policy
    const [policyType, setPolicyType] = useState<PolicyOption>(actionTypeOptions[0]);
    const [alarmType, setAlarmType] = useState(policy ? policy.alarmType : "");
    const [actions, loading, error] = usePolicyActionsByType(policyType.value);
    const [addedActions, setAddedActions] = useState<PolicyAction[]>(policy ? policy.policies : []);
    const [search, setSearch] = useState("");
    const [upsertPolicy] = useMutation<AlarmPolicy>(UPSERT_POLICY);
    const actionsRef = useRef<PolicyAction[]>([]);
    const clearForm = useCallback(() => {
        setPolicyType(actionTypeOptions[0]);
        setAlarmType("");
        setAddedActions([]);
    }, []);

    const onClose = useCallback(() => {
        toggle();
        clearForm();
    }, [clearForm, toggle]);

    const onCreate = useCallback(() => {
        const policyData: AlarmPolicyInput = {
            companySysId: selectedCompany ? selectedCompany.id : "",
            alarmType: alarmType ? alarmType : "",
            type: policyType.value,
            policies: addedActions.map((a) => ({ name: a.name, policy: a.id! }))
        };

        setAddLoading(true);
        upsertPolicy({
            variables: {
                policy: policyData
            },
            refetchQueries: [
                {
                    query: GET_ALARM_POLICIES,
                    variables: { companyId: selectedCompany?.id }
                }
            ]
        })
            .then(() => {
                PolicyToaster.show({
                    message: `Successfully Created the Policy.`,
                    intent: "success"
                });
            })
            .catch((e) => {
                PolicyToaster.show({
                    message: `Failed to Created the Policy.`,
                    intent: "danger"
                });
            })
            .finally(() => {
                setAddLoading(false);

                setTimeout(onClose, 500);
            });
    }, [addedActions, alarmType, onClose, policyType.value, selectedCompany, upsertPolicy]);

    const re = new RegExp(search, "gi");

    useEffect(() => {
        if (policy) {
            setAddedActions(policy.policies);
            setAlarmType(policy.alarmType);
            setPolicyType(getPolicyType(policy));
            actionsRef.current = policy.policies;
        }
    }, [policy]);

    return (
        <Dialog
            icon="new-object"
            onClose={onClose}
            title={`Create Policy for Company: ${selectedCompany?.name}`}
            isOpen={isOpen}
        >
            <div className={Classes.DIALOG_BODY}>
                <FormGroup
                    key="form_alarm_type"
                    label="Alarm Type"
                    labelFor="text-input"
                    labelInfo={"*"}
                    disabled={isEdit}
                >
                    <InputGroup
                        disabled={isEdit}
                        key="input_alarm_type"
                        value={alarmType}
                        onChange={(e) => setAlarmType(e.target.value)}
                    />
                </FormGroup>

                <FormGroup key={`select_policy_type`} label={"Policy Type"} labelFor="text-input" disabled={isEdit}>
                    <TypeSelect
                        disabled={isEdit}
                        filterable={false}
                        itemRenderer={renderType}
                        items={actionTypeOptions}
                        onItemSelect={(t) => {
                            setPolicyType(t);
                            setAddedActions([]);
                        }}
                        noResults={<MenuItem disabled={true} text="No results." />}
                        popoverProps={{ minimal: true, popoverClassName: "custom-select" }}
                    >
                        <Button disabled={isEdit} text={policyType.text} rightIcon="caret-down" />
                    </TypeSelect>
                </FormGroup>
                <Pre>
                    <H4>Policy Actions</H4>
                    <FormGroup key="policy search">
                        <InputGroup
                            leftIcon="search"
                            key="input_alarm_type"
                            placeholder="Search..."
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                        />
                    </FormGroup>
                    <div className="policy-cards">
                        {loading ? (
                            <CustomLoader />
                        ) : (
                            actions
                                .filter(
                                    (a) =>
                                        re.test(a.name) ||
                                        (a.roles && re.test(a.roles.toString())) ||
                                        re.test(JSON.stringify(a.filter)) ||
                                        (a.evaluationPolicy && re.test(JSON.stringify(a.evaluationPolicy)))
                                )
                                .map((a) => {
                                    const isAdded = addedActions.some((action) => a.id === action.id);
                                    return (
                                        <Card className={isAdded ? "selected" : undefined}>
                                            <H5>{a.name}</H5>
                                            <H6>{a.project.name}</H6>
                                            <p>{a.type}</p>
                                            <div className="add-btn">
                                                {isAdded ? (
                                                    <Button
                                                        onClick={() =>
                                                            setAddedActions(
                                                                addedActions.filter((action) => action.id !== a.id)
                                                            )
                                                        }
                                                        minimal
                                                        icon="small-minus"
                                                        intent="danger"
                                                        text="Remove Action"
                                                    />
                                                ) : (
                                                    <Button
                                                        onClick={() => setAddedActions([...addedActions, a])}
                                                        minimal
                                                        icon="small-plus"
                                                        intent="success"
                                                        text="Add Action"
                                                    />
                                                )}
                                            </div>
                                        </Card>
                                    );
                                })
                        )}
                    </div>
                </Pre>
            </div>
            <div className={Classes.DIALOG_FOOTER}>
                <div className={Classes.DIALOG_FOOTER_ACTIONS}>
                    <Button
                        disabled={!alarmType || addLoading}
                        loading={addLoading}
                        text={isEdit ? "Update" : "Create"}
                        intent={isEdit ? "warning" : "primary"}
                        onClick={onCreate}
                    ></Button>
                </div>
            </div>
        </Dialog>
    );
}

const actionTypeOptions: PolicyOption[] = [
    { text: "Alarm Policy", value: "actionpolicyrel" },
    { text: "Remediation Policy", value: "remediationpolicyrel" }
];

const getPolicyType = (p: AlarmPolicy) => {
    return p.type === "action policy" || p.type === "actionpolicyrel" ? actionTypeOptions[0] : actionTypeOptions[1];
};
